// ABOUTME: ApolloLink capping how many GraphQL queries are in flight at once.
// ABOUTME: Excess queries wait in a FIFO queue; mutations bypass the cap.
import { ApolloLink } from "@apollo/client"
import { Observable } from "@apollo/client/utilities"
import { isQueryOperation } from "./operationKind"
import { mirrorSettling } from "./mirrorSettling"

/**
 * Каждый запрос на бэке занимает соединение из пула Postgres (10 на под), и
 * залп из сотен запросов продвигается через FIFO-очередь пула «строем» — ни
 * один не завершается, пока не рассосётся вся пачка, страдают все пользователи
 * (инцидент 17.08.2026). Лимит на клиенте держит очередь у нас: первые ответы
 * приходят за сотни миллисекунд и рендерятся прогрессивно, а пул сервера
 * остаётся доступным другим. Мутации не ограничиваются — они интерактивные,
 * единичные и не должны ждать позади длинной очереди чтений.
 */
export const DEFAULT_MAX_CONCURRENT_QUERIES = 8

export function createConcurrencyLimitLink(maxConcurrent: number = DEFAULT_MAX_CONCURRENT_QUERIES): ApolloLink {
  let active = 0
  const queue: (() => void)[] = []

  const release = () => {
    active -= 1
    const next = queue.shift()
    if (next) next()
  }

  return new ApolloLink((operation, forward) => {
    if (!isQueryOperation(operation.query)) {
      return forward(operation)
    }

    return new Observable((observer) => {
      let started = false
      let settled = false
      let subscription: { unsubscribe: () => void } | null = null

      const settle = () => {
        if (started && !settled) {
          settled = true
          release()
        }
      }

      const start = () => {
        started = true
        active += 1
        subscription = mirrorSettling(forward(operation), observer, settle)
      }

      const queued = () => start()
      if (active < maxConcurrent) {
        start()
      } else {
        queue.push(queued)
      }

      return () => {
        if (started) {
          subscription?.unsubscribe()
          settle()
        } else {
          const index = queue.indexOf(queued)
          if (index >= 0) queue.splice(index, 1)
        }
      }
    })
  })
}
